import { Button } from 'antd';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { fadeInUp } from 'react-animations';
import packageJson from '../../../package.json';

import {
    IconConsent,
    IconDI,
    IconDM,
    IconDSM,
    IconPN,
    IconNewTab,
    IconSetting,
    IconRiskManagement,
    IconCookie,
    IconDPIA,
    IconDataInquiry,
    IconDataBreach,
} from '../../assets/Images/icons/icon-landing';
import HeaderLayout from '../../components/layout/header';
import ModalConfirm from '../../components/Shared/modal/confirm';

import { ROUTER } from '../../constants/router';
import GlobalService from '../../services/Global.service';
import { filterNull } from '../../util/util';
import { MAP_LINK, MENU_KEY } from './json_data';
import { usePermissionContext } from '../../context/permission-context';
import UserService from '../../services/UserService';
import ModalRelease from './components/ModalRelease';
import Cookies from 'js-cookie';

const fadeInUpAnimation = keyframes`${fadeInUp}`;
const FadeInUpDiv = styled.div`
    animation: ${(props) => props.duration} ${fadeInUpAnimation};
`;

const LandingPage = () => {
    const { t, i18n } = useTranslation('landing');
    const navigate = useNavigate();
    const [openDs, setOpenDs] = useState(false);
    const [loadingURL, setLoadingURL] = useState(false);

    const permissionContext = usePermissionContext();
    const permission = useMemo(
        () => permissionContext?.permission,
        [permissionContext?.permission]
    );

    const [loading, setLoading] = useState(true);
    const [releaseList, setReleaseList] = useState([]);
    const [openRelease, setOpenRelease] = useState(false);

    const landingMenuItems = useMemo(
        () => [
            {
                keyPermission: MENU_KEY.DATA_CATALOG,
                title: 'Data Catalog',
                text: t('landing.des1'),
                icon: <IconDM />,
                // onClick: () => setOpenDs(true),
                onClick: () => setOpenDs(true),
                linkText: t('StartNow'),
                duration: '1s',
            },
            {
                keyPermission: MENU_KEY.DATA_INVENTORY,
                title: 'Data Inventory',
                text: t('landing.des4'),
                icon: <IconDI />,
                onClick: () => navigate(ROUTER.DS_INVENTORY.DASHBOARD),
                linkText: t('StartNow'),
                duration: '1.2s',
            },
            {
                keyPermission: MENU_KEY.DATA_SUBJECT_MANAGEMENT,
                title: 'Data Subject 360',
                text: t('landing.des2'),
                icon: <IconDSM />,
                onClick: () => navigate(ROUTER.DS_360.DASHBOARD),
                linkText: t('StartNow'),
                duration: '1.4s',
            },
            {
                keyPermission: MENU_KEY.RISK_MANAGEMENT,
                title: 'Risk Management',
                text: t('landing.des7'),
                icon: <IconRiskManagement />,
                onClick: () => navigate(ROUTER.RISK_MANAGEMENT.DASHBOARD),
                linkText: t('StartNow'),
                duration: '1.4s',
            },
            {
                keyPermission: MENU_KEY.DPIA,
                title: 'DPIA',
                text: t('landing.des11'),
                icon: <IconDPIA />,
                onClick: () => navigate(ROUTER.DPIA.DEFAULT),
                linkText: t('StartNow'),
                duration: '1.5s',
            },
            {
                keyPermission: MENU_KEY.PRIVACY_POLICY,
                title: 'Privacy Policy',
                text: t('landing.des8'),
                icon: <IconPN />,
                onClick: () => navigate(ROUTER.POLICY.DEFAULT),
                linkText: t('StartNow'),
                duration: '1.6s',
            },
            {
                keyPermission: MENU_KEY.CONSENT_MANAGEMENT,
                title: 'Consent Management',
                text: t('landing.des9'),
                icon: <IconConsent />,
                onClick: () => navigate(ROUTER.CONSENT_MANAGEMENT.DASHBOARD),
                linkText: t('StartNow'),
                duration: '1.7s',
            },
            {
                keyPermission: MENU_KEY.COOKIE_MANAGEMENT,
                title: 'Cookie Management',
                text: t('landing.des10'),
                icon: <IconCookie />,
                onClick: () => navigate(ROUTER.COOKIE_MANAGEMENT.DASHBOARD),
                linkText: t('StartNow'),
                duration: '1.8s',
            },
            {
                keyPermission: MENU_KEY.DATA_INQUIRY,
                title: 'Data Inquiry',
                text: t('landing.DataInquiry'),
                icon: <IconDataInquiry />,
                onClick: () => navigate(ROUTER.DATA_INQUIRY.DEFAULT),
                linkText: t('StartNow'),
                duration: '1.9s',
            },
            {
                keyPermission: MENU_KEY.DATA_BREACH,
                title: 'Data Breach Management',
                text: t('landing.Data Breach Management'),
                icon: <IconDataBreach />,
                onClick: () => navigate(ROUTER.DATA_BREACH.DASHBOARD),
                linkText: t('StartNow'),
                duration: '2s',
            },
            {
                keyPermission: MENU_KEY.CONFIGURATION,
                title: 'Configuration',
                text: t('landing.des6'),
                icon: <IconSetting />,
                onClick: () => navigate(ROUTER.CONFIGURATION.USER_MANAGEMENT),
                linkText: t('StartNow'),
                duration: '2s',
            },
        ],
        [navigate, t]
    );

    const [menuList, setMenuList] = useState([]);

    useEffect(() => {
        setLoading(true);
    }, []);

    const newLandingMenuItems = useCallback(
        (parsedPermission) => {
            const getSubMenuWithPermission = (
                subMenuList,
                menuPermissionItem
            ) => {
                return subMenuList.find((subMenu) =>
                    hasSubMenuPermission(
                        menuPermissionItem.subMenuList,
                        subMenu
                    )
                );
            };

            const hasSubMenuPermission = (subMenuList, subMenu) => {
                return subMenuList.find(
                    (subMenuPermission) =>
                        subMenuPermission.subMenuName === subMenu.subMenuName
                )?.permission;
            };

            const getMenuPermissionItem = (parsedPermission, item) => {
                return parsedPermission.find(
                    (menu) => menu.mainMenuName === item.keyPermission
                );
            };

            const getSubMenuList = (item) => {
                return MAP_LINK.find(
                    (menu) => menu.mainMenuName === item.keyPermission
                )?.subMenuList;
            };

            const createMenuItem = (item, parsedPermission) => {
                const menuPermissionItem = getMenuPermissionItem(
                    parsedPermission,
                    item
                );

                if (
                    !menuPermissionItem?.permission ||
                    !menuPermissionItem?.packagePermission
                )
                    return null;

                const subMenuList = getSubMenuList(item);
                if (!subMenuList) return item;

                const firstSubMenuWithPermission = getSubMenuWithPermission(
                    subMenuList,
                    menuPermissionItem
                );
                if (!firstSubMenuWithPermission) return null;

                return {
                    ...item,
                    onClick: () => navigate(firstSubMenuWithPermission.link),
                };
            };

            const newLandingMenuItems = landingMenuItems.map((item) =>
                createMenuItem(item, parsedPermission)
            );

            return filterNull(newLandingMenuItems);
        },
        [landingMenuItems, navigate]
    );

    useEffect(() => {
        if (permission) {
            setMenuList(newLandingMenuItems(permission));
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    }, [landingMenuItems, newLandingMenuItems, permission]);

    const switchPathDataHub = async () => {
        setLoadingURL(true);
        try {
            const { data } = await GlobalService.getDatahubURL();
            const url = data?.resultData?.datahubUrl;
            await setOpenDs(false);
            await setLoadingURL(false);
            window.open(url);
        } catch (error) {
            console.log(error);
        }
    };
    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    };

    const getRelease = async () => {
        try {
            const { data } = await GlobalService.getReleaseList();
            if (data?.resultData) {
                setReleaseList(data?.resultData);
                const versionStr = data?.resultData[0].version;
                const versionNo = versionStr.split(' - ')[0];
                if (!getCookie('isRelease')) {
                    Cookies.set('isReleaseVersion', packageJson.version, {
                        expires: 365,
                        path: '/',
                    });
                    Cookies.set('isRelease', 'true', {
                        expires: 365,
                        path: '/',
                    });
                    setOpenRelease(true);
                } else {
                    if (getCookie('isReleaseVersion') !== packageJson.version) {
                        Cookies.set('isReleaseVersion', packageJson.version, {
                            expires: 365,
                            path: '/',
                        });
                        Cookies.set('isRelease', 'true', {
                            expires: 365,
                            path: '/',
                        });
                        setOpenRelease(true);
                    }
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handelClearRelease = async () => {
        // Cookies.set('isRelease', 'true', { expires: 365, path: '/' });
        setOpenRelease(false);
    };

    useEffect(() => {
        const endDate = UserService.getEndDate()?.split('/');
        const startDate = UserService.getStartDate()?.split('/');
        const date = new Date(new Date().setHours(0, 0, 0, 0));
        if (endDate && startDate) {
            let expiredDate = new Date(
                +endDate[2],
                endDate[1] - 1,
                +endDate[0]
            );
            let effectiveDate = new Date(
                +startDate[2],
                startDate[1] - 1,
                +startDate[0]
            );
            if (date < effectiveDate) {
                navigate('/package-not-available');
            } else if (date >= expiredDate) {
                navigate('/package-expired');
            }
        } else return navigate(ROUTER.USER_NOT_FOUND);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        getRelease();
    }, []);

    return (
        <>
            <div className="bg-gray-body min-h-screen pb-24">
                <HeaderLayout />
                <div className="w-full text-center">
                    {menuList.length > 0 && (
                        <FadeInUpDiv
                            duration="2s"
                            className="text-xl py-12 px-4"
                        >
                            {t('landing.Please Select Activities below to')}
                            <strong> {t('landing.Begin Your Process')}</strong>
                        </FadeInUpDiv>
                    )}
                    {!loading && menuList.length === 0 && (
                        <>
                            <div className="w-max m-auto mt-60">
                                <svg
                                    width="194"
                                    height="135"
                                    viewBox="0 0 194 135"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M138.626 91.8159C138.626 103.968 131.402 108.211 122.49 108.211C113.579 108.211 106.355 103.968 106.355 91.8159C106.355 79.6641 122.49 64.2051 122.49 64.2051C122.49 64.2051 138.626 79.6641 138.626 91.8159Z"
                                        fill="#65707A"
                                    />
                                    <path
                                        d="M122.068 96.1818L128.946 83.6001L122.094 94.5865L122.168 90.0137L126.908 80.911L122.188 88.8034L122.322 80.579L127.397 73.3323L122.343 79.2858L122.426 64.2051L121.924 83.3141L116.784 75.4469L121.862 84.9267L121.381 94.1118L121.367 93.8681L115.418 85.5559L121.349 94.7294L121.289 95.8781L121.278 95.8955L121.283 95.9897L120.063 108.247H121.693L121.888 107.257L127.805 98.1057L121.903 106.352L122.068 96.1818Z"
                                        fill="#3F3D56"
                                    />
                                    <path
                                        d="M193.883 52.7435C193.883 75.9565 180.083 84.0616 163.06 84.0616C146.038 84.0616 132.238 75.9565 132.238 52.7435C132.238 29.5306 163.06 0 163.06 0C163.06 0 193.883 29.5306 193.883 52.7435Z"
                                        fill="#F2F2F2"
                                    />
                                    <path
                                        d="M161.938 80.5107L162.254 61.0836L175.391 37.0494L162.303 58.036L162.445 49.3007L171.499 31.9123L162.483 46.9891L162.738 31.2784L172.433 17.4351L162.778 28.808L162.938 0L161.935 38.1365L162.018 36.5633L152.16 21.4748L161.86 39.5832L160.941 57.1294L160.914 56.6638L149.55 40.7854L160.88 58.309L160.765 60.5034L160.744 60.5364L160.753 60.7165L158.423 105.233H161.537L161.91 82.2392L173.212 64.7586L161.938 80.5107Z"
                                        fill="#3F3D56"
                                    />
                                    <path
                                        d="M192.618 101.346C192.618 108.274 147.673 134.276 95.1329 134.276C42.5924 134.276 0 120.819 0 113.891C0 106.962 41.547 115.198 94.0874 115.198C146.628 115.198 192.618 94.4175 192.618 101.346Z"
                                        fill="#3F3D56"
                                    />
                                    <path
                                        opacity="0.1"
                                        d="M192.618 101.346C192.618 108.274 147.673 134.276 95.1329 134.276C42.5924 134.276 0 120.819 0 113.891C0 106.962 41.547 115.198 94.0874 115.198C146.628 115.198 192.618 94.4175 192.618 101.346Z"
                                        fill="black"
                                    />
                                    <path
                                        d="M192.618 101.346C192.618 108.274 147.673 126.436 95.1329 126.436C42.5924 126.436 0 120.819 0 113.891C0 106.963 41.547 107.357 94.0874 107.357C146.628 107.357 192.618 94.4175 192.618 101.346Z"
                                        fill="#3F3D56"
                                    />
                                    <path
                                        opacity="0.1"
                                        d="M96.1978 118.332C118.509 118.332 136.596 116.933 136.596 115.207C136.596 113.481 118.509 112.082 96.1978 112.082C73.8863 112.082 55.7993 113.481 55.7993 115.207C55.7993 116.933 73.8863 118.332 96.1978 118.332Z"
                                        fill="black"
                                    />
                                    <path
                                        d="M64.1938 52.5911C64.1938 52.5911 76.0208 52.8101 73.8306 54.8908C71.6404 56.9714 63.4272 54.8908 63.4272 54.8908L64.1938 52.5911Z"
                                        fill="#A0616A"
                                    />
                                    <path
                                        d="M39.552 27.319C39.552 27.319 44.3271 26.4663 45.9874 30.3211C47.6477 34.176 51.0576 45.1697 51.0576 45.1697C51.0576 45.1697 60.1828 51.21 61.8426 51.0115C63.5023 50.813 66.3397 51.218 65.8192 53.0287C65.2988 54.8395 65.5298 56.8927 64.3316 57.1444C63.1334 57.396 61.2277 54.9939 59.7693 56.1509C58.3108 57.3079 45.313 53.0819 44.4786 52.2062C43.6441 51.3306 36.7095 32.3698 36.7095 32.3698C36.7095 32.3698 36.9869 27.2572 39.552 27.319Z"
                                        fill="#7B67FB"
                                    />
                                    <path
                                        d="M49.7931 108.715L50.3406 113.314L45.7413 114.519L43.113 113.314V109.262L49.7931 108.715Z"
                                        fill="#A0616A"
                                    />
                                    <path
                                        d="M37.4188 111.672L37.9663 116.271L33.367 117.476L30.7388 116.271V112.219L37.4188 111.672Z"
                                        fill="#A0616A"
                                    />
                                    <path
                                        d="M50.2311 58.4497L52.3118 66.0058L55.378 81.3371C55.378 81.3371 56.2541 85.0604 54.502 89.5503C52.7498 94.0401 49.5741 108.714 50.2311 109.59C50.8882 110.466 42.7845 111.124 42.894 110.247C43.0035 109.371 44.0986 97.3254 44.0986 97.3254C44.0986 97.3254 45.6317 86.703 46.3983 85.3889C47.1649 84.0748 42.894 80.7895 42.894 80.7895C42.894 80.7895 41.7989 93.3831 40.1563 94.4782C38.5137 95.5732 38.7327 112.547 37.9661 112.766C37.1995 112.985 31.6146 113.861 30.9575 112.766C30.3005 111.671 30.9575 93.3831 30.9575 93.3831C30.9575 93.3831 34.6809 79.4754 33.9143 77.5043C33.1477 75.5331 33.5858 67.1009 33.5858 67.1009C33.5858 67.1009 30.7385 60.6399 32.2717 57.2451L50.2311 58.4497Z"
                                        fill="#2F2E41"
                                    />
                                    <path
                                        d="M49.9026 16.3987C49.9026 16.3987 45.3033 22.0932 45.5223 24.6119C45.7413 27.1306 38.2947 20.0125 38.2947 20.0125C38.2947 20.0125 43.6606 12.7849 43.6606 11.2518C43.6606 9.71868 49.9026 16.3987 49.9026 16.3987Z"
                                        fill="#A0616A"
                                    />
                                    <path
                                        d="M49.8482 18.534C53.8399 18.534 57.0758 15.2981 57.0758 11.3064C57.0758 7.31476 53.8399 4.07886 49.8482 4.07886C45.8565 4.07886 42.6206 7.31476 42.6206 11.3064C42.6206 15.2981 45.8565 18.534 49.8482 18.534Z"
                                        fill="#A0616A"
                                    />
                                    <path
                                        d="M31.6333 54.3533C31.3058 55.5732 30.8996 56.9563 30.9576 58.1215C30.9948 58.876 32.9978 59.371 35.7672 59.6951C38.3352 59.9963 41.5625 60.1496 44.4962 60.2284C47.5767 60.3117 50.3308 60.3117 51.6548 60.3117C55.5971 60.3117 52.4214 57.7929 50.8882 56.5884C49.3551 55.3838 49.6836 38.3004 49.7931 36.1102C49.9027 33.92 48.8076 27.5685 48.8076 26.3639C48.8076 25.1593 46.2308 22.0153 46.2308 22.0153C46.2308 22.0153 45.6318 23.7357 43.2226 21.436C40.8134 19.1363 37.6376 18.4792 37.6376 18.4792C35.2285 19.3553 31.1766 28.9921 30.9576 30.6347C30.8689 31.3016 30.9423 34.7172 31.0912 38.6168C31.3069 44.3168 31.6836 51.0549 31.9432 51.77C32.148 52.3339 31.9213 53.2801 31.6333 54.3533Z"
                                        fill="#7B67FB"
                                    />
                                    <path
                                        d="M43.3323 60.7495L44.0988 63.4873L41.142 66.5535L40.1565 62.2826L43.3323 60.7495Z"
                                        fill="#A0616A"
                                    />
                                    <path
                                        d="M44.3177 112.11C44.3177 112.11 46.6174 114.738 48.3695 112.986C50.1217 111.234 50.0121 110.138 50.3407 110.138C50.6692 110.138 55.5971 114.519 55.5971 114.519C55.5971 114.519 64.0293 116.49 59.4299 117.914C54.8305 119.337 41.799 118.352 41.799 117.914C41.799 117.476 41.4705 111.672 42.6751 111.672L44.3177 112.11Z"
                                        fill="#2F2E41"
                                    />
                                    <path
                                        d="M31.9437 115.067C31.9437 115.067 34.2433 117.695 35.9955 115.943C37.7476 114.191 37.6381 113.095 37.9667 113.095C38.2952 113.095 43.2231 117.476 43.2231 117.476C43.2231 117.476 51.6553 119.447 47.0559 120.871C42.4565 122.294 29.425 121.309 29.425 120.871C29.425 120.433 29.0964 114.629 30.301 114.629L31.9437 115.067Z"
                                        fill="#2F2E41"
                                    />
                                    <path
                                        d="M41.7333 17.0758C41.4998 17.2266 41.1959 17.3795 40.9621 17.2291C40.0929 15.0614 39.4432 12.812 39.0228 10.5147C38.8752 9.70775 38.7727 8.81069 39.221 8.12368C39.3978 7.85273 39.6524 7.63115 39.7819 7.33466C39.978 6.88566 39.8489 6.36593 39.914 5.88031C40.0506 4.86294 41.0123 4.15335 41.9794 3.80921C42.9464 3.46507 43.9968 3.35832 44.9186 2.90674C45.7939 2.47795 46.4949 1.76683 47.3066 1.22719C48.1182 0.687551 49.1579 0.318582 50.0618 0.683231C50.8614 1.00582 51.3595 1.82087 52.1153 2.23587C52.6282 2.51749 53.2234 2.59585 53.7959 2.71674C55.387 3.05433 56.8809 3.74839 58.1652 4.7467C58.4776 4.96959 58.747 5.24733 58.9603 5.56643C59.9302 7.16325 58.1937 9.33421 58.9598 11.0382L57.3035 9.73125C56.8612 9.34435 56.3624 9.02732 55.8243 8.79114C55.2756 8.58332 54.6259 8.55823 54.1275 8.86777C53.4281 9.3022 53.2385 10.2098 52.9942 10.996C52.7498 11.7823 52.2096 12.6542 51.3865 12.634C50.2672 12.6066 49.8757 11.0445 48.8712 10.5499C48.2163 10.2275 47.392 10.4398 46.8338 10.91C46.2755 11.3802 45.9406 12.0625 45.6949 12.7497C45.5413 13.1792 45.3973 13.6634 45.1246 14.0357C44.8238 14.4463 44.4392 14.4793 44.1179 14.7859C43.2818 15.5841 42.7677 16.4075 41.7333 17.0758Z"
                                        fill="#2F2E41"
                                    />
                                    <path
                                        opacity="0.1"
                                        d="M31.6336 54.3534C32.9948 56.3278 34.529 58.3647 35.7676 59.6952C38.3356 59.9964 41.5628 60.1497 44.4965 60.2285C43.9724 59.5179 43.3171 58.9142 42.5659 58.4501C41.1423 57.574 37.7475 47.1707 37.7475 47.1707C37.7475 47.1707 41.9089 36.4388 43.0039 32.387C44.099 28.3351 39.8282 26.0355 39.8282 26.0355C37.857 24.3928 34.4622 28.2256 34.4622 28.2256C34.4622 28.2256 32.7298 33.4514 31.0916 38.6169C31.3073 44.3169 31.684 51.055 31.9435 51.7701C32.1483 52.334 31.9216 53.2802 31.6336 54.3534Z"
                                        fill="black"
                                    />
                                    <path
                                        d="M39.4992 25.1595C39.4992 25.1595 43.7701 27.4592 42.675 31.511C41.5799 35.5628 37.4186 46.2947 37.4186 46.2947C37.4186 46.2947 40.8134 56.698 42.237 57.5741C43.6606 58.4502 45.6317 60.5309 44.0986 61.626C42.5655 62.721 41.4704 64.4732 40.3753 63.9256C39.2802 63.3781 39.2802 60.3118 37.4186 60.3118C35.5569 60.3118 28.0008 48.9229 27.8913 47.7183C27.7818 46.5137 34.1333 27.3497 34.1333 27.3497C34.1333 27.3497 37.5281 23.5168 39.4992 25.1595Z"
                                        fill="#7B67FB"
                                    />
                                    <path
                                        d="M128.307 55.0915L127.075 53.8596C126.356 53.141 125.382 52.7373 124.365 52.7373C123.349 52.7373 122.375 53.141 121.656 53.8596L97.7601 77.7555L73.8641 53.8596C73.1455 53.141 72.1709 52.7373 71.1547 52.7373C70.1385 52.7373 69.1639 53.141 68.4453 53.8596L67.2133 55.0915C66.8575 55.4474 66.5753 55.8697 66.3827 56.3346C66.1902 56.7995 66.0911 57.2978 66.0911 57.801C66.0911 58.3041 66.1902 58.8024 66.3827 59.2673C66.5753 59.7322 66.8575 60.1546 67.2133 60.5104L91.1093 84.4063L67.2133 108.302C66.8575 108.658 66.5753 109.08 66.3827 109.545C66.1902 110.01 66.0911 110.509 66.0911 111.012C66.0911 111.515 66.1902 112.013 66.3827 112.478C66.5753 112.943 66.8575 113.365 67.2133 113.721L68.4453 114.953C69.1639 115.672 70.1385 116.075 71.1547 116.075C72.1709 116.075 73.1455 115.672 73.8641 114.953L97.7601 91.0571L121.656 114.953C122.375 115.672 123.349 116.075 124.365 116.075C125.382 116.075 126.356 115.672 127.075 114.953L128.307 113.721C129.025 113.003 129.429 112.028 129.429 111.012C129.429 109.995 129.025 109.021 128.307 108.302L104.411 84.4063L128.307 60.5104C129.025 59.7918 129.429 58.8172 129.429 57.801C129.429 56.7847 129.025 55.8101 128.307 55.0915Z"
                                        fill="#FF6584"
                                    />
                                    <path
                                        opacity="0.1"
                                        d="M66.3599 109.601L89.5462 86.4148L89.323 86.1917L67.2126 108.302C66.8423 108.672 66.552 109.114 66.3599 109.601Z"
                                        fill="black"
                                    />
                                    <path
                                        opacity="0.1"
                                        d="M66.8822 55.8683C67.6008 55.1498 68.5754 54.7461 69.5916 54.7461C70.6079 54.7461 71.5825 55.1498 72.3011 55.8683L96.197 79.7643L120.093 55.8683C120.812 55.1498 121.786 54.7461 122.802 54.7461C123.819 54.7461 124.793 55.1498 125.512 55.8683L126.744 57.1003C127.272 57.6289 127.635 58.3006 127.786 59.0326C127.938 59.7646 127.872 60.525 127.597 61.22L128.306 60.5104C128.662 60.1546 128.944 59.7322 129.137 59.2673C129.329 58.8024 129.428 58.3041 129.428 57.801C129.428 57.2978 129.329 56.7995 129.137 56.3346C128.944 55.8698 128.662 55.4473 128.306 55.0915L127.074 53.8596C126.356 53.141 125.381 52.7373 124.365 52.7373C123.349 52.7373 122.374 53.141 121.655 53.8596L97.7594 77.7555L73.8634 53.8596C73.1448 53.141 72.1702 52.7373 71.154 52.7373C70.1378 52.7373 69.1632 53.141 68.4446 53.8596L67.2126 55.0915C66.8423 55.4615 66.552 55.9038 66.3599 56.3907L66.8822 55.8683Z"
                                        fill="black"
                                    />
                                    <path
                                        opacity="0.1"
                                        d="M104.633 84.6292L102.848 86.4147L126.744 110.311C127.272 110.839 127.635 111.511 127.786 112.243C127.938 112.975 127.872 113.735 127.597 114.43L128.306 113.721C128.662 113.365 128.944 112.943 129.137 112.478C129.329 112.013 129.429 111.514 129.429 111.011C129.429 110.508 129.329 110.01 129.137 109.545C128.944 109.08 128.662 108.658 128.306 108.302L104.633 84.6292Z"
                                        fill="black"
                                    />
                                    <path
                                        d="M27.8245 100.562C27.8245 110.654 21.8247 114.178 14.4235 114.178C7.02231 114.178 1.02246 110.654 1.02246 100.562C1.02246 90.4692 14.4235 77.6299 14.4235 77.6299C14.4235 77.6299 27.8245 90.4692 27.8245 100.562Z"
                                        fill="#65707A"
                                    />
                                    <path
                                        d="M14.0728 104.188L19.7848 93.7382L14.0944 102.863L14.1561 99.0649L18.0927 91.5047L14.1725 98.0598L14.2834 91.2291L18.4987 85.2102L14.3008 90.1551L14.3701 77.6299L13.9531 93.5007L9.68444 86.9667L13.9014 94.8398L13.5022 102.469L13.4903 102.266L8.54956 95.3626L13.4752 102.981L13.4253 103.936L13.4163 103.95L13.4205 104.028L12.4073 114.208H13.761L13.9235 113.386L18.8371 105.786L13.9357 112.634L14.0728 104.188Z"
                                        fill="#3F3D56"
                                    />
                                </svg>
                            </div>
                            <p className="mt-6 text-2xl font-semibold">
                                You don't have permission
                            </p>
                        </>
                    )}
                    <div className="container mx-auto px-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                            {menuList.map((item) => {
                                return (
                                    <FadeInUpDiv
                                        duration={item.duration}
                                        className="grid grid-rows-2 mx-auto group w-full max-w-md px-8 rounded-2xl transform duration-500 hover:-translate-y-2 cursor-pointer bg-white"
                                        key={item.keyPermission}
                                    >
                                        <div className="w-32 h-20 m-auto">
                                            {item.icon}
                                        </div>
                                        <div className="pb-6">
                                            <div className="text-[17px] font-semibold py-4">
                                                {item.title}
                                            </div>
                                            <p className="text-xs text-[#65707A]">
                                                {item.text}
                                            </p>
                                        </div>
                                        <div className="mb-8">
                                            <Button
                                                onClick={item.onClick}
                                                size="large"
                                                type="primary"
                                                className={classNames({
                                                    'px-8':
                                                        i18n.language === 'th',
                                                })}
                                            >
                                                {item.linkText}
                                            </Button>
                                        </div>
                                    </FadeInUpDiv>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <ModalConfirm
                    title={t('landing.modalCon')}
                    text={t('landing.modalDsDes')}
                    icon={<IconNewTab />}
                    loading={loadingURL}
                    open={openDs}
                    handelOk={() => {
                        switchPathDataHub();
                    }}
                    handelClose={() => setOpenDs(false)}
                />
            </div>
            <ModalRelease
                loading={loading}
                open={openRelease}
                handelClose={() => setOpenRelease(false)}
                handelClearRelease={handelClearRelease}
                releaseList={releaseList}
                title={t('whats news')}
            />
        </>
    );
};

export default LandingPage;
